import { useContext, useState, useCallback, useRef, useEffect } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { toSnakeCase } from '../../utils';
// Helper to generate SQL for a measure
const generateMeasureSql = (measure) => {
    let sql = measure.sql || '';
    if (measure.calculationType === 'DROPDOWN' && measure.measureFunction && measure.dimension) {
        // Format: function(dimension), e.g., "max(response_value)"
        // Map function names to lowercase SQL function names
        const functionMap = {
            Count: 'count',
            Sum: 'sum',
            Average: 'avg',
            Min: 'min',
            Max: 'max',
        };
        const sqlFunction = functionMap[measure.measureFunction] || measure.measureFunction.toLowerCase();
        if (sqlFunction === 'count') {
            sql = `${sqlFunction}(distinct ${measure.dimension})`;
        }
        else {
            sql = `${sqlFunction}(${measure.dimension})`;
        }
    }
    return sql;
};
export const useDataViews = () => {
    const { entityId } = useContext(AuthContext);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [isLoading, setIsLoading] = useState(false);
    // Use refs to store latest values without causing callback recreation
    const translateRef = useRef(translate);
    const addNotificationRef = useRef(addNotification);
    const libraryClientRef = useRef(libraryClient);
    const entityIdRef = useRef(entityId);
    useEffect(() => {
        translateRef.current = translate;
        addNotificationRef.current = addNotification;
        libraryClientRef.current = libraryClient;
        entityIdRef.current = entityId;
    }, [translate, addNotification, libraryClient, entityId]);
    const fetchDataViews = useCallback(async (showArchived = false) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Fetch active or archived views based on the parameter
            const response = await libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                entityId: entityIdRef.current,
                active: showArchived ? 'false' : 'true',
            });
            return response.data || [];
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error fetching datasets'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to load datasets'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const fetchDataViewById = useCallback(async (viewId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.getResourceById(viewId, ResourceType.CUBE_CONFIG);
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error fetching dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to load dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const createDataView = useCallback(async (formData) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const createRequest = {
                name: formData.name,
                type: ResourceType.CUBE_CONFIG,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    name: toSnakeCase(formData.name),
                    sql: '',
                    dimensions: {},
                    measures: {},
                },
                metadata: {
                    description: formData.description || '',
                    full_name: formData.name,
                    short_name: toSnakeCase(formData.name),
                },
            };
            const resource = await libraryManager.createResource(createRequest, ResourceType.CUBE_CONFIG);
            addNotificationRef.current({
                title: translateRef.current('Dataset created successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error creating dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to create dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateDataView = useCallback(async (viewId, updates) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const updateRequest = {
                data: updates.data,
                metadata: updates.metadata,
            };
            if (updates.name) {
                updateRequest.name = updates.name;
                updateRequest.metadata = {
                    ...(updates.metadata || {}),
                    full_name: updates.name,
                    short_name: toSnakeCase(updates.name),
                };
            }
            if (updates.status) {
                updateRequest.status = updates.status;
            }
            // Cast needed because UpdateResourceRequest doesn't include 'name', but the API accepts it
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, updateRequest);
            addNotificationRef.current({
                title: translateRef.current('Dataset updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deleteDataView = useCallback(async (viewId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            await libraryManager.archiveResource(viewId, ResourceType.CUBE_CONFIG);
            addNotificationRef.current({
                title: translateRef.current('Dataset deleted successfully'),
                type: 'success',
            });
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const archiveDataView = useCallback(async (dataView) => {
        setIsLoading(true);
        try {
            // Use LibraryManager.archiveResource which makes a DELETE request and sets active: false
            const archiveLibraryManager = new LibraryManager({
                axiosInstance: libraryClientRef.current,
            });
            const archivedView = await archiveLibraryManager.archiveResource(dataView.id, ResourceType.CUBE_CONFIG);
            addNotificationRef.current({
                title: translateRef.current('Dataset archived successfully'),
                type: 'success',
            });
            return archivedView;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error archiving dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to archive dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const cloneDataView = useCallback(async (sourceView, option = 'CLONE', overrides) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Create a new view with cloned data
            const clonedName = overrides?.name || sourceView.name;
            const clonedDescription = overrides?.description ?? sourceView.metadata?.description ?? '';
            const sourceData = (sourceView.data || {});
            // Remove studyId and environmentId as they are not part of the dataset configuration
            // (they were only used for preview purposes and should not be persisted)
            const { studyId: _studyId, environmentId: _envId, ...cleanedSourceData } = sourceData;
            const createRequest = {
                name: clonedName,
                type: ResourceType.CUBE_CONFIG,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    ...cleanedSourceData,
                    name: toSnakeCase(clonedName), // data.name should be snake_cased
                },
                metadata: {
                    description: clonedDescription,
                },
            };
            // If creating a new version, include the key (source view's key or ID) in the payload
            // The key is used to group versions of the same dataset together
            if (option === 'CREATE_VERSION') {
                // Use the source view's key if it exists, otherwise use its ID
                createRequest.key = sourceView.key || sourceView.id;
            }
            const resource = await libraryManager.createResource(createRequest, ResourceType.CUBE_CONFIG);
            addNotificationRef.current({
                title: option === 'CREATE_VERSION'
                    ? translateRef.current('Dataset version created successfully')
                    : translateRef.current('Dataset cloned successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: option === 'CREATE_VERSION'
                    ? translateRef.current('Error creating dataset version')
                    : translateRef.current('Error cloning dataset'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to clone dataset'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const checkViewNameUnique = useCallback(async (name, excludeId) => {
        try {
            const views = await fetchDataViews();
            const isUnique = !views.some((view) => view.name.toLowerCase() === name.toLowerCase() && view.id !== excludeId);
            return isUnique;
        }
        catch (error) {
            // If fetch fails, assume unique to allow user to proceed
            return true;
        }
    }, [fetchDataViews]);
    const addDimension = useCallback(async (viewId, dimension) => {
        setIsLoading(true);
        try {
            // First fetch the current view to get existing dimensions
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
                [dimension.name]: {
                    sql: dimension.sql,
                    type: dimension.type,
                    title: dimension.name,
                    description: dimension.description,
                },
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const addDimensions = useCallback(async (viewId, dimensions, sqlQuery, currentView) => {
        setIsLoading(true);
        try {
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            // Add all new dimensions at once
            for (const dimension of dimensions) {
                const dimensionKey = toSnakeCase(dimension.name);
                updatedDimensions[dimensionKey] = {
                    sql: dimension.sql,
                    type: dimension.type,
                    title: dimension.title || dimension.name, // Use provided title or fallback to name
                    description: dimension.description,
                };
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                    ...(sqlQuery ? { sql: sqlQuery } : {}),
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimensions added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding dimensions'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add dimensions'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateDimension = useCallback(async (viewId, dimensionName, dimension) => {
        setIsLoading(true);
        try {
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            // If name changed, remove old and add new
            if (dimensionName !== dimension.name) {
                delete updatedDimensions[dimensionName];
            }
            updatedDimensions[dimension.name] = {
                sql: dimension.sql,
                type: dimension.type,
                title: dimension.name,
                description: dimension.description,
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const updateDimensions = useCallback(async (viewId, dimensions, sqlQuery, currentView) => {
        setIsLoading(true);
        try {
            // Build new dimensions object from the provided dimensions
            const updatedDimensions = {};
            for (const dimension of dimensions) {
                const dimensionKey = toSnakeCase(dimension.name);
                updatedDimensions[dimensionKey] = {
                    sql: dimension.sql,
                    type: dimension.type,
                    title: dimension.title || dimension.name, // Use provided title or fallback to name
                    description: dimension.description,
                };
            }
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                    ...(sqlQuery ? { sql: sqlQuery } : {}),
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimensions updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating dimensions'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update dimensions'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deleteDimension = useCallback(async (viewId, dimensionName) => {
        setIsLoading(true);
        try {
            const currentView = await fetchDataViewById(viewId);
            const updatedDimensions = {
                ...(currentView.data?.dimensions || {}),
            };
            delete updatedDimensions[dimensionName];
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    dimensions: updatedDimensions,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Dimension deleted successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting dimension'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete dimension'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    const addMeasure = useCallback(async (viewId, measure, currentView) => {
        setIsLoading(true);
        try {
            // Generate SQL based on calculation type
            const sql = generateMeasureSql(measure);
            const measureKey = toSnakeCase(measure.name);
            const isPredefined = measure.calculationType === 'DROPDOWN';
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
                [measureKey]: {
                    sql,
                    type: 'number', // Measures are typically numbers
                    title: measure.name,
                    description: measure.description,
                    format: isPredefined ? 'number' : undefined,
                    meta: {
                        isPredefined,
                    },
                },
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure added successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error adding measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to add measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const updateMeasure = useCallback(async (viewId, measureName, measure, currentView) => {
        setIsLoading(true);
        try {
            // Generate SQL based on calculation type
            const sql = generateMeasureSql(measure);
            // Preserve the existing measure's title (display name), not the snake_cased key
            const existingMeasure = currentView.data?.measures?.[measureName];
            const isPredefined = measure.calculationType === 'DROPDOWN';
            const updatedMeasures = {
                ...(currentView.data?.measures || {}),
                [measureName]: {
                    sql,
                    type: 'number', // Measures are typically numbers
                    title: existingMeasure?.title || measure.name, // Preserve existing title or use measure.name as fallback
                    description: measure.description,
                    format: isPredefined ? 'number' : undefined,
                    meta: {
                        isPredefined,
                    },
                },
            };
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure updated successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error updating measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to update measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deleteMeasure = useCallback(async (viewId, measureName) => {
        setIsLoading(true);
        try {
            // First fetch the current view to get existing measures
            const currentView = await fetchDataViewById(viewId);
            const updatedMeasures = { ...(currentView.data?.measures || {}) };
            delete updatedMeasures[measureName];
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(viewId, ResourceType.CUBE_CONFIG, {
                data: {
                    ...currentView.data,
                    measures: updatedMeasures,
                },
            });
            addNotificationRef.current({
                title: translateRef.current('Measure deleted successfully'),
                type: 'success',
            });
            return resource;
        }
        catch (error) {
            const axiosError = error;
            addNotificationRef.current({
                title: translateRef.current('Error deleting measure'),
                subtitle: axiosError.response?.data?.message || translateRef.current('Failed to delete measure'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [fetchDataViewById]);
    return {
        fetchDataViews,
        fetchDataViewById,
        createDataView,
        updateDataView,
        deleteDataView,
        archiveDataView,
        cloneDataView,
        checkViewNameUnique,
        addDimension,
        addDimensions,
        updateDimension,
        updateDimensions,
        deleteDimension,
        addMeasure,
        updateMeasure,
        deleteMeasure,
        isLoading,
    };
};
